/**
 * Kitaplik / katalog.
 *
 * ── FILTRE URL'DE TUTULUYOR ──────────────────────────────────────────────────
 * Arama ve filtreler `useSearchParams` ile adres cubugunda. Boylece kullanici
 * filtrelenmis bir listeyi paylasabilir, geri tusu beklendigi gibi calisir ve
 * sayfa yenilendiginde secim kaybolmaz.
 *
 * ── ARAMA GECIKMELI ──────────────────────────────────────────────────────────
 * Her tusa basista istek atmak, "kode" yazan kullanici icin dort istek demek.
 * 300 ms gecikme, yazma hizinin altinda kalir ve tek istek uretir.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { categoryHue } from './Home';
import './Library.css';
import { Button, Card, EmptyState, ErrorState, LoadingBlock, PageHeader } from '@/components/ui';
import { SalesSections } from '@/components/SalesSections';
import { canonicalCatalogName, catalogNameLanguage, slugifyTr } from '@/lib/format';
import { catalogBanner, presentationFor } from '@/lib/gameCatalog';
import { copy, localizeCatalogName, useI18n } from '@/lib/i18n';
import { quoteRequestPath } from '@/lib/quote';
import type { CatalogItem, CategoryContent } from '@shared/index';

/**
 * Kesfet — magaza vitrini (Epic ana sayfa duzeni).
 *
 * ── "BENDEKILER" SEKMESI YOK ─────────────────────────────────────────────────
 * Sayfa bir kutuphane degil kesif yuzeyi (kullanici karari, 19 Agu 2026):
 * sekme ve sayfa ici arama kaldirildi; filtreleme yalnizca kategori
 * kenar cubugu baglantilariyla. Sayfa içi kategori hapları bilinçli olarak
 * yoktur; sahiplik kartin kunyesinde
 * gorunur; "kitapligin" ana sayfada yasar. Ust cubuktaki arama hapi `q`
 * parametresiyle buraya dusurur.
 */
export default function Library() {
  const [params, setParams] = useSearchParams();
  const [activeArea, setActiveArea] = useState(0);
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);

  const category = params.get('kategori') ?? '';
  const q = params.get('q') ?? '';

  function update(key: string, value: string) {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  }

  const catalog = useQuery({
    queryKey: ['catalog', { category, q }],
    queryFn: () =>
      api.get<{ items: CatalogItem[] }>('/catalog', {
        ownedOnly: false,
        ...(category ? { category } : {}),
        ...(q ? { q } : {}),
      }),
  });

  /* Epic magaza duzeni BU sayfanin sistemi: vitrin (hero + sag ray) yalnizca
     filtresiz kesif gorunumunde. Kategori sayfasi ve arama, icerige odaklanir. */
  const showBanner = !category && !q;

  /* Kategori secildiginde satis anlatimi da gelir: urunler NE oldugunu, bu
     bolumler NASIL satin alindigini anlatir. Icerik yonetim panelinden
     yazilir; yayinda degilse sorgu `null` doner ve hicbir sey cizilmez. */
  const salesContent = useQuery({
    queryKey: ['category-content', category],
    queryFn: () =>
      api.get<{ content: CategoryContent | null }>(
        `/catalog/categories/${slugifyTr(category)}/content`,
      ),
    enabled: Boolean(category),
    staleTime: 5 * 60_000,
  });

  /* Servisler ic urun/engine katmanidir; musteri vitrininin kategorisi degildir.
     Sunucu da filtreler, bu savunma eski onbellek yanitlarinin sizmasini onler. */
  const items = (catalog.data?.items ?? []).filter((item) => item.category !== 'Servisler');

  const activeCategory = DISCOVERY_AREAS[activeArea]!.category;
  const displayedItems = showBanner
    ? items.filter((item) => canonicalCatalogName(item.category) === activeCategory)
    : items;
  const hasFilters = Boolean(category || q);

  return (
    <div className="lib-page stack stack--lg flow-inset">
      {showBanner ? (
        <>
          <PageHeader title={c('Keşfet', 'Discover')} description={c('Kurumunuz için hazırlanmış deneyimleri ve yeni katalog kategorilerini inceleyin.', 'Explore experiences and new catalog categories prepared for your organization.')} />
          <DiscoveryShowcase active={activeArea} onActive={setActiveArea} onCategory={(value) => update('kategori', value)} />
        </>
      ) : q ? (
        <PageHeader
          title={`${c('Arama', 'Search')}: ${q}`}
          description={c('Katalogda eşleşen ürünler.', 'Matching products in the catalog.')}
        />
      ) : null}

      {category ? <CategoryIntro category={category} count={items.length} items={items} /> : null}

      {!catalog.isLoading && !catalog.isError && displayedItems.some((item) => canonicalCatalogName(item.category) === 'Leds Play') ? (
        <section className="lib-package-offer" aria-label={c('Leds Play paketi', 'Leds Play package')}>
          <div>
            <h2>{c('Leds Play: tüm oyunlar tek pakette', 'Leds Play: all games in one package')}</h2>
            <p>{c('Paket, Leds Play kataloğundaki tüm oyunları kapsar. Oyunları ayrı ayrı satın almanız gerekmez.', 'The package includes every game in the Leds Play catalog. Games do not need to be purchased individually.')}</p>
          </div>
          <Link className="btn btn--primary btn--md" to={quoteRequestPath({ solution: 'Leds Play', catalog: 'Leds Play', source: '/kesfet' })}>{c('Teklif Al', 'Request a Quote')}</Link>
        </section>
      ) : null}

      {catalog.isLoading ? (
        <LoadingBlock rows={4} label={c('Katalog yükleniyor', 'Loading catalog')} />
      ) : catalog.isError ? (
        <ErrorState message={c('Katalog alınamadı.', 'Catalog could not be loaded.')} onRetry={() => void catalog.refetch()} />
      ) : displayedItems.length === 0 ? (
        <Card>
          <EmptyState
            title={hasFilters ? c('Eşleşen ürün yok', 'No matching products') : c('Bu katalogda gösterilecek içerik yok', 'No content to show in this catalog')}
            description={
              hasFilters ? c('Farklı bir kategori veya arama deneyin.', 'Try another category or search.') : c('Üstteki alandan başka bir katalog seçebilirsiniz.', 'You can select another catalog above.')
            }
            action={
              hasFilters ? (
                <Button variant="secondary" onClick={() => setParams(new URLSearchParams())}>
                  {c('Filtreleri temizle', 'Clear filters')}
                </Button>
              ) : undefined
            }
          />
        </Card>
      ) : hasFilters ? (
        /* Kesif filtresi (chip/arama): buyuk kapakli izgara. Kullaniciya
           TANIMLI katalogun calisma alani ayri sayfada: /katalog/:kategori. */
        <div className="lib-category-grid">
          {displayedItems.map((item) => (
            <DiscoverProductCard key={item.id} item={item} />
          ))}
        </div>
      ) : (
        <section className="lib-section" aria-label={localizeCatalogName(activeCategory, language)}>
          <div className="lib-section__head">
            <div>
              <h2>{localizeCatalogName(activeCategory, language)}</h2>
              <p>{displayedItems.length} {c('içerik', 'items')}</p>
            </div>
            <button type="button" className="lib-section__all" onClick={() => update('kategori', activeCategory)}>{c('Tümünü gör →', 'View all →')}</button>
          </div>
          <div className="lib-section__grid lib-section__grid--landscape">{displayedItems.slice(0, 8).map((item) => <DiscoverProductCard key={item.id} item={item} />)}</div>
        </section>
      )}

      {/* Satis anlatimi urunlerin ALTINDA: once ne aldigini gor, sonra nasil
          alindigini oku. Yalnizca kategori gorunumunde — aramada anlamsiz. */}
      {category && salesContent.data?.content ? (
        <SalesSections
          sales={salesContent.data.content.sales}
          hue={categoryHue(category)}
          category={category}
        />
      ) : null}

    </div>
  );
}

const DISCOVERY_AREAS = [
  {
    name: 'Leds Play',
    nameEn: 'Leds Play',
    category: 'Leds Play',
    title: 'Şehir ekranını ortak bir oyun alanına dönüştürün.',
    titleEn: 'Turn a city screen into a shared play space.',
    text: 'Ziyaretçiler uygulama indirmeden telefonlarıyla katılır; içerik, cihaz ve oturum akışı Hub’dan yönetilir.',
    textEn: 'Visitors join with their phones without installing an app; Hub manages content, devices and sessions.',
    image: '/media/games-v2/carpisan-araba-banner.webp',
  },
  {
    name: 'Etkileşimli Dijital Sanat',
    nameEn: 'Immersive Art',
    category: 'Immersive Digital Art',
    title: 'Hareketle değişen bir sahne kurun.',
    titleEn: 'Build a stage that responds to movement.',
    text: 'Motion Track ve gerçek zamanlı görsel sistemlerle performansı mekânın tamamına taşıyın.',
    textEn: 'Bring performance to the entire venue with Motion Track and real-time visual systems.',
    image: '/media/immersive-v2/gelgit-odasi.webp',
  },
  {
    name: 'VR Deneyimleri',
    nameEn: 'VR Experiences',
    category: 'VR Deneyimleri',
    title: 'Lisanslı VR içeriklerini tek merkezden yönetin.',
    titleEn: 'Manage licensed VR content from one place.',
    text: 'Satın alınan deneyimler CORTEX kitaplığına gelir; cihazda yüklenir, güncellenir ve çalıştırılır.',
    textEn: 'Purchased experiences arrive in the CORTEX library, then install, update and run on the device.',
    image: '/media/vr-v2/vr-arena.webp',
  },
  {
    name: 'AR Deneyimleri',
    nameEn: 'AR Experiences',
    category: 'AR Deneyimleri',
    title: 'Fiziksel mekâna dijital bir katman ekleyin.',
    titleEn: 'Add a digital layer to a physical venue.',
    text: 'Müze, etkinlik ve marka alanları için mekâna bağlı artırılmış gerçeklik deneyimleri tasarlayın.',
    textEn: 'Create location-based augmented reality experiences for museums, events and branded spaces.',
    image: '/media/ar-v2/dinozor-parki-ar.webp',
  },
] as const;

function DiscoveryShowcase({ active, onActive, onCategory }: { active: number; onActive: (index: number) => void; onCategory: (category: string) => void }) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const [autoPlay, setAutoPlay] = useState(
    () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  const area = DISCOVERY_AREAS[active]!;

  useEffect(() => {
    if (!autoPlay) return;
    const timer = window.setTimeout(
      () => onActive((active + 1) % DISCOVERY_AREAS.length),
      6500,
    );
    return () => window.clearTimeout(timer);
  }, [active, autoPlay, onActive]);

  return (
    <section className="discover-showcase" aria-label={c('Wondeon teknoloji alanları', 'Wondeon technology areas')}>
      <div className="discover-showcase__visual">
        <img key={area.image} src={area.image} alt="" />
        <div className="discover-showcase__overlay">
          <div className="discover-showcase__copy">
            <h2>{c(area.title, area.titleEn)}</h2>
            <p>{c(area.text, area.textEn)}</p>
            <button type="button" className="btn btn--inverse btn--md" onClick={() => onCategory(area.category)}>{c('Kataloğu incele', 'Explore catalog')}</button>
          </div>
        </div>
        <button
          type="button"
          className="discover-showcase__playback"
          onClick={() => setAutoPlay((value) => !value)}
          aria-label={autoPlay ? c('Otomatik geçişi durdur', 'Pause automatic rotation') : c('Otomatik geçişi başlat', 'Start automatic rotation')}
          aria-pressed={!autoPlay}
        >
          {autoPlay ? <PauseIcon /> : <PlayIcon />}
        </button>
      </div>
      <div className="discover-showcase__rail" aria-label={c('Teknoloji alanı seçimi', 'Select technology area')}>
        {DISCOVERY_AREAS.map((item, index) => (
          <button key={item.name} type="button" className={index === active ? 'is-active' : ''} aria-pressed={index === active} onClick={() => onActive(index)}>
            <img src={item.image} alt="" loading={index === 0 ? 'eager' : 'lazy'} />
            <span>{c(item.name, item.nameEn)}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function PauseIcon() {
  return <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true"><path d="M5 3.5v9M11 3.5v9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></svg>;
}

function PlayIcon() {
  return <svg viewBox="0 0 16 16" width="16" height="16" fill="none" aria-hidden="true"><path d="m5.5 3.5 6.5 4.5-6.5 4.5v-9Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /></svg>;
}

function CategoryIntro({
  category,
  count,
  items,
}: {
  category: string;
  count: number;
  items: CatalogItem[];
}) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const area = DISCOVERY_AREAS.find((item) => item.category === category);
  const image = area?.image ?? (items[0] ? catalogBanner(items[0]) : null);
  const displayCategory = canonicalCatalogName(category);
  const localizedCategory = localizeCatalogName(displayCategory, language);
  return (
    <section className="lib-category-hero" style={{ '--pcard-hue': categoryHue(category) } as React.CSSProperties}>
      {image ? <img src={image} alt="" /> : null}
      <div className="lib-category-hero__shade" />
      <div className="lib-category-hero__content">
        <Link className="lib-category-hero__back" to="/kesfet">← {c('Keşfet', 'Discover')}</Link>
        <p>{c('Katalog', 'Catalog')}</p>
        <h1 lang={catalogNameLanguage(displayCategory)}>{localizedCategory}</h1>
        <span>{area ? c(area.text, area.textEn) : c('Bu katalogdaki deneyimleri, teslim seçeneklerini ve kullanım alanlarını inceleyin.', 'Explore the experiences, delivery options and use cases in this catalog.')}</span>
        <small>{count} {c('içerik', 'items')}</small>
      </div>
    </section>
  );
}

function DiscoverProductCard({ item }: { item: CatalogItem }) {
  const { language } = useI18n();
  const c = (tr: string, en: string) => copy(language, tr, en);
  const image = catalogBanner(item);
  const summary = language === 'tr' ? presentationFor(item.slug)?.short ?? item.summary : item.summary;
  const category = canonicalCatalogName(item.category);
  return (
    <Link className="discover-card" to={`/kesfet/${item.slug}`}>
      <span className="discover-card__visual">{image ? <img src={image} alt="" loading="eager" /> : null}</span>
      <span className="discover-card__body">
        <span className="discover-card__category" lang={catalogNameLanguage(category)}>{localizeCatalogName(category, language)}</span>
        <strong>{item.name}</strong>
        <span>{summary}</span>
        <small>{item.entitled ? c('Hesabınıza tanımlı', 'Assigned to your account') : category === 'Leds Play' ? c('Leds Play paketine dahil', 'Included in the Leds Play package') : c('Teklif Al', 'Request a Quote')}</small>
      </span>
    </Link>
  );
}
